import { lazy, Suspense, useDeferredValue, useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useMatch, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { MAJOR_KEY_OPTIONS, MINOR_KEY_OPTIONS, parseChart, transposeChart } from "../../../shared/chords";
import { formatWhen } from "../../../shared/time";
import type { Arrangement, SongDetail } from "../../../shared/types";
import { useSession } from "../../shell/session";
import { Badge, Dialog, Empty, ErrorNote } from "../../ui";
import { ChartView } from "../stand/ChartView";
import {
  FILE_LIMIT,
  archiveSong,
  createArrangement,
  createSong,
  deleteAttachment,
  fileSize,
  getSong,
  listSongs,
  messageOf,
  toggleFavorite,
  typedFile,
  updateArrangement,
  updateSong,
  uploadAttachment,
  type SongWrite,
} from "./music-api";
import { songLibraryStatus } from "./library-state";
import { CHART_MODES, parseTags, safeKey, standHref, writtenKey, type ChartMode } from "./music-theory";
import "./songs.css";

const PdfChart = lazy(() => import("../stand/pdf-chart").then((mod) => ({ default: mod.PdfChart })));

const STARTER = `{title: New song}
{key: G}

## Verse 1
[G]Write the line in your [D]own words
[Em]Keep the chords beside the [C]syllables`;

const DRAFT_KEY = "mb-live:new-song-draft:v1";

interface NewSongDraft {
  title: string;
  authors: string;
  ccli: string;
  defaultKey: string;
  bpm: string;
  meter: string;
  tags: string;
  notes: string;
  chordpro: string;
}

function readNewSongDraft(): Partial<NewSongDraft> | null {
  try {
    const value = window.localStorage.getItem(DRAFT_KEY);
    return value ? JSON.parse(value) as Partial<NewSongDraft> : null;
  } catch {
    return null;
  }
}

function titleFromChart(chart: string): string {
  return chart.match(/^\s*\{\s*title\s*:\s*([^}]+)\}/im)?.[1]?.trim() ?? "";
}

function canEdit(role: string | undefined): boolean {
  return role === "owner" || role === "leader";
}

function KeyField({ label, value, onChange, empty, disabled = false, id }: { label: string; value: string; onChange: (value: string) => void; empty?: string; disabled?: boolean; id?: string }) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <select id={id} aria-label={label} value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)}>
        {empty && <option value="">{empty}</option>}
        <optgroup label="Major">{MAJOR_KEY_OPTIONS.map((key) => <option key={key} value={key}>{key}</option>)}</optgroup>
        <optgroup label="Minor">{MINOR_KEY_OPTIONS.map((key) => <option key={key} value={key}>{key}</option>)}</optgroup>
      </select>
    </div>
  );
}

function bpmOrNull(value: string): number | null {
  if (!value.trim()) return null;
  const number = Number(value);
  if (!Number.isInteger(number) || number < 20 || number > 300) throw new Error("BPM must be a whole number from 20 to 300, or left blank.");
  return number;
}

function meterOrThrow(value: string): string {
  const meter = value.trim();
  if (!/^\d{1,2}\/\d{1,2}$/.test(meter)) throw new Error("Meter should look like 4/4 or 6/8.");
  return meter;
}

export function SongsPage() {
  const { user } = useSession();
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const tag = params.get("tag") ?? "";
  const key = params.get("key") ?? "";
  const favorite = params.get("favorite") === "1";
  const [draft, setDraft] = useState(q);
  const [page, setPage] = useState(1);
  const [songs, setSongs] = useState<Awaited<ReturnType<typeof listSongs>>["songs"]>([]);
  const [total, setTotal] = useState(0);
  const [libraryTotal, setLibraryTotal] = useState<number | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const editor = canEdit(user?.role);
  const filtersActive = Boolean(q.trim() || tag.trim() || key || favorite);
  const library = songLibraryStatus({ libraryTotal, matchTotal: total, filtersActive });
  const newSongAction = () => (editor ? <Link className="btn primary" to="/songs/new">New song</Link> : undefined);

  useEffect(() => setDraft(q), [q]);
  useEffect(() => {
    const next = draft.trim();
    if (next === q) return;
    const handle = window.setTimeout(() => write({ q: next || null }), 220);
    return () => window.clearTimeout(handle);
  }, [draft, q]);

  function write(next: Record<string, string | null>) {
    setPage(1);
    setParams((current) => {
      const paramsNext = new URLSearchParams(current);
      for (const [name, value] of Object.entries(next)) {
        if (value) paramsNext.set(name, value);
        else paramsNext.delete(name);
      }
      return paramsNext;
    }, { replace: true });
  }

  useEffect(() => {
    let current = true;
    const handle = window.setTimeout(() => {
      setLoading(true);
      void listSongs({ q, tag, key, favorite, page })
        .then((result) => {
          if (!current) return;
          setTotal(result.total);
          setLibraryTotal(result.libraryTotal);
          setSongs((existing) => {
            const rows = page === 1 ? result.songs : [...existing, ...result.songs];
            const seen = new Set<string>();
            return rows.filter((song) => {
              if (seen.has(song.id)) return false;
              seen.add(song.id);
              return true;
            });
          });
          setError("");
        })
        .catch((caught: unknown) => {
          if (current) setError(messageOf(caught));
        })
        .finally(() => {
          if (current) setLoading(false);
        });
    }, 160);
    return () => {
      current = false;
      window.clearTimeout(handle);
    };
  }, [q, tag, key, favorite, page]);

  const tags = useMemo(() => [...new Set(songs.flatMap((song) => song.tags))].slice(0, 12), [songs]);

  async function flipFavorite(songId: string, isFavorite: boolean) {
    try {
      const result = await toggleFavorite(songId);
      setSongs((existing) => existing.flatMap((song) => {
        if (song.id !== songId) return [song];
        if (favorite && !result.favorite) return [];
        return [{ ...song, favorite: result.favorite }];
      }));
      if (favorite && isFavorite) setTotal((count) => Math.max(0, count - 1));
    } catch (caught) {
      setError(messageOf(caught));
    }
  }

  return (
    <section className="page">
      <header className="page-head">
        <div>
          <p className="kicker">Library</p>
          <h1 className="page-title">Songs</h1>
          <p className="lede">Original charts for Moksha Base. Search, transpose, and open any song on the stand.</p>
        </div>
        {newSongAction()}
      </header>
      <form className="panel panel-pad song-filters" onSubmit={(event) => { event.preventDefault(); write({ q: draft.trim() || null }); }}>
        <div className="field"><label htmlFor="song-q">Search</label><input id="song-q" value={draft} placeholder="Title, writer, or tag" autoComplete="off" onChange={(event) => setDraft(event.target.value)} /></div>
        <KeyField id="song-key" label="Key" value={key} empty="Any key" onChange={(value) => write({ key: value || null })} />
        <div className="field"><label htmlFor="song-tag">Tag</label><input id="song-tag" value={tag} placeholder="Any tag" autoComplete="off" onChange={(event) => write({ tag: event.target.value.trim() || null })} /></div>
        <button className={favorite ? "btn teal" : "btn"} type="button" aria-pressed={favorite} onClick={() => write({ favorite: favorite ? null : "1" })}>Favourites</button>
        <button className="btn" type="submit">Search</button>
      </form>
      {tags.length > 0 && (
        <div className="tag-row">
          {tags.map((item) => <button key={item} className={tag === item ? "btn small primary" : "btn small ghost"} type="button" onClick={() => write({ tag: tag === item ? null : item })}>{item}</button>)}
        </div>
      )}
      {error && <ErrorNote>{error}</ErrorNote>}
      <div className="panel panel-pad">
        <div className="split"><strong>{loading && songs.length === 0 ? "Looking up songs…" : library.countLabel}</strong><span className="faint">Keys and tempos stay with the chart</span></div>
        <div className="song-list">
          {songs.map((song) => (
            <div className="song-row" key={song.id}>
              <Link className="song-open" to={`/songs/${song.id}`}>
                <span className="key-mark">{song.defaultKey}</span>
                <span>
                  <strong className="song-title">{song.title}</strong>
                  <span className="muted">{song.authors || "No writer listed"} · {song.bpm ?? "—"} bpm · {song.meter}</span>
                </span>
              </Link>
              <span className="tag-row">
                {song.favorite && <Badge tone="violet">Favourite</Badge>}
                {song.tags.slice(0, 3).map((item) => <Badge key={item}>{item}</Badge>)}
              </span>
              <span className="song-actions">
                <button className="btn small ghost" type="button" aria-pressed={song.favorite} onClick={() => void flipFavorite(song.id, song.favorite)}>{song.favorite ? "Unfavourite" : "Favourite"}</button>
                <Link className="btn small" to={standHref(song.id)}>Stand</Link>
              </span>
            </div>
          ))}
        </div>
        {!loading && !error && songs.length === 0 && library.title && (
          <Empty title={library.title} body={library.body} action={newSongAction()} />
        )}
        {songs.length < total && <button className="btn" type="button" onClick={() => setPage((current) => current + 1)}>Load more</button>}
      </div>
    </section>
  );
}

export function SongEditorPage() {
  const creating = Boolean(useMatch("/songs/new"));
  const draft = useMemo(() => creating ? readNewSongDraft() : null, [creating]);
  const { songId = "" } = useParams();
  const navigate = useNavigate();
  const { user } = useSession();
  const editor = canEdit(user?.role);
  const [song, setSong] = useState<SongDetail | null>(null);
  const [title, setTitle] = useState(draft?.title ?? "");
  const [authors, setAuthors] = useState(draft?.authors ?? (creating ? "Moksha Base Writers" : ""));
  const [ccli, setCcli] = useState(draft?.ccli ?? "");
  const [defaultKey, setDefaultKey] = useState(draft?.defaultKey ?? "G");
  const [bpm, setBpm] = useState(draft?.bpm ?? "");
  const [meter, setMeter] = useState(draft?.meter ?? "4/4");
  const [tags, setTags] = useState(draft?.tags ?? (creating ? "gathering" : ""));
  const [notes, setNotes] = useState(draft?.notes ?? "");
  const [chordpro, setChordpro] = useState(draft?.chordpro ?? (creating ? STARTER : ""));
  const [arrangementId, setArrangementId] = useState("");
  const [chartName, setChartName] = useState("Standard");
  const [chartKey, setChartKey] = useState("G");
  const [chartBpm, setChartBpm] = useState("");
  const [chartMeter, setChartMeter] = useState("4/4");
  const [target, setTarget] = useState("G");
  const [capo, setCapo] = useState(0);
  const [mode, setMode] = useState<ChartMode>("stacked");
  const [label, setLabel] = useState("");
  const [showPdf, setShowPdf] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [addingChart, setAddingChart] = useState(false);

  useEffect(() => {
    if (!creating) return;
    try {
      window.localStorage.setItem(DRAFT_KEY, JSON.stringify({ title, authors, ccli, defaultKey, bpm, meter, tags, notes, chordpro } satisfies NewSongDraft));
    } catch {
      // Song creation still works if browser storage is unavailable.
    }
  }, [creating, title, authors, ccli, defaultKey, bpm, meter, tags, notes, chordpro]);

  function applySong(next: SongDetail, preferredId = arrangementId) {
    const arrangement = next.arrangements.find((item) => item.id === preferredId) ?? next.arrangements.find((item) => item.isDefault) ?? next.arrangements[0];
    setSong(next);
    setTitle(next.title);
    setAuthors(next.authors);
    setCcli(next.ccli ?? "");
    setDefaultKey(next.defaultKey);
    setBpm(next.bpm?.toString() ?? "");
    setMeter(next.meter);
    setTags(next.tags.join(", "));
    setNotes(next.notes);
    if (arrangement) fillArrangement(arrangement, next.defaultKey);
  }

  function fillArrangement(arrangement: Arrangement, fallbackKey: string) {
    setArrangementId(arrangement.id);
    setChartName(arrangement.name);
    setChartKey(arrangement.keyName);
    setChartBpm(arrangement.bpm?.toString() ?? "");
    setChartMeter(arrangement.meter);
    setChordpro(arrangement.chordpro);
    setTarget(arrangement.keyName || fallbackKey);
  }

  useEffect(() => {
    if (creating || !songId) return;
    let current = true;
    void getSong(songId).then((next) => { if (current) applySong(next, ""); }).catch((caught: unknown) => { if (current) setError(messageOf(caught)); });
    return () => { current = false; };
  }, [creating, songId]);

  const deferredChart = useDeferredValue(chordpro);
  const sourceKey = writtenKey(deferredChart, creating ? defaultKey : chartKey);
  const preview = useMemo(() => {
    try {
      return transposeChart(deferredChart, sourceKey, safeKey(target, sourceKey), capo);
    } catch {
      return null;
    }
  }, [deferredChart, sourceKey, target, capo]);
  const pdf = song?.attachments.find((file) => file.kind === "chart-pdf") ?? null;

  async function saveDetails(event: FormEvent) {
    event.preventDefault();
    setError("");
    setBusy(true);
    try {
      const payload: SongWrite = {
        title: title.trim() || (creating ? titleFromChart(chordpro) : ""),
        authors: authors.trim(),
        ccli: ccli.trim() || null,
        bpm: bpmOrNull(bpm),
        meter: meterOrThrow(meter),
        defaultKey,
        tags: parseTags(tags),
        notes,
        ...(creating ? { arrangementName: chartName.trim() || "Standard", chordpro } : {}),
      };
      if (!payload.title) throw new Error("Add a title above, or paste a {title: ...} line in the chart.");
      if (creating) {
        const created = await createSong(payload);
        try { window.localStorage.removeItem(DRAFT_KEY); } catch { /* Storage may be unavailable. */ }
        navigate(`/songs/${created.id}`);
        return;
      }
      const saved = await updateSong(songId, payload);
      setSong(saved);
      setTitle(saved.title);
      setAuthors(saved.authors);
      setCcli(saved.ccli ?? "");
      setDefaultKey(saved.defaultKey);
      setBpm(saved.bpm?.toString() ?? "");
      setMeter(saved.meter);
      setTags(saved.tags.join(", "));
      setNotes(saved.notes);
      setStatus("Song details saved");
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setBusy(false);
    }
  }

  async function saveChart(event: FormEvent) {
    event.preventDefault();
    if (!arrangementId) return;
    setBusy(true);
    setError("");
    try {
      await updateArrangement(songId, arrangementId, {
        name: chartName.trim() || "Chart",
        keyName: chartKey,
        bpm: bpmOrNull(chartBpm),
        meter: meterOrThrow(chartMeter),
        chordpro,
      });
      applySong(await getSong(songId), arrangementId);
      setStatus("Chart saved");
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setBusy(false);
    }
  }

  async function addArrangement(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      const created = await createArrangement(songId, {
        name: String(data.get("name") || "New chart"),
        keyName: String(data.get("keyName") || defaultKey),
        chordpro: data.get("copy") === "on" ? chordpro : `{key: ${String(data.get("keyName") || defaultKey)}}\n\n## Verse 1\n`,
        isDefault: false,
      });
      applySong(await getSong(songId), created?.id ?? arrangementId);
      setAddingChart(false);
      setStatus("Arrangement added");
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setBusy(false);
    }
  }

  async function makeDefault() {
    if (!arrangementId) return;
    setBusy(true);
    try {
      await updateArrangement(songId, arrangementId, { isDefault: true });
      applySong(await getSong(songId), arrangementId);
      setStatus("Default arrangement updated");
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setBusy(false);
    }
  }

  async function onUpload(file: File) {
    const typed = typedFile(file);
    if (typed.size > FILE_LIMIT) {
      setError("Files must be 12 MB or smaller.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await uploadAttachment(songId, typed, label);
      setSong(await getSong(songId));
      setLabel("");
      setStatus("File added");
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="page">
      <header className="page-head">
        <div>
          <p className="kicker">{creating ? "New song" : song?.defaultKey ?? "Song"}</p>
          <h1 className="page-title">{creating ? "New song" : title || "Song"}</h1>
          {!creating && song && <p className="lede">{song.authors || "No writer listed"} · updated {formatWhen(song.updatedAt, { day: "numeric", month: "short", year: "numeric" })}</p>}
        </div>
        <div className="cluster">
          {!creating && <button className="btn" type="button" onClick={() => void toggleFavorite(songId).then((result) => setSong((current) => current ? { ...current, favorite: result.favorite } : current)).catch((caught: unknown) => setError(messageOf(caught)))}>{song?.favorite ? "Favourited" : "Favourite"}</button>}
          {!creating && <Link className="btn teal" to={standHref(songId)}>Open on the stand</Link>}
          {!creating && editor && <button className="btn danger" type="button" onClick={() => setConfirmArchive(true)}>Archive</button>}
        </div>
      </header>
      {error && <ErrorNote>{error}</ErrorNote>}
      {status && <p className="status-line" role="status">{status}</p>}
      {!editor && creating && <Empty title="Leaders add songs" body="Your role can open the library and the stand. Song editing stays with leaders and owners." />}
      {(editor || !creating) && (
        <div className="grid-2 song-editor-layout">
          <form className="panel panel-pad form-grid song-editor-form" noValidate onSubmit={(event) => void saveDetails(event)}>
            <div className="field"><label htmlFor="title">Title</label><input id="title" value={title} onChange={(event) => setTitle(event.target.value)} disabled={!editor} placeholder="Song title" /></div>
            <div className="field"><label htmlFor="authors">Writers</label><input id="authors" value={authors} onChange={(event) => setAuthors(event.target.value)} disabled={!editor} /></div>
            <div className="cluster">
              <KeyField label="Default key" value={defaultKey} onChange={setDefaultKey} disabled={!editor} />
              <div className="field"><label htmlFor="bpm">BPM</label><input id="bpm" inputMode="numeric" value={bpm} placeholder="80" onChange={(event) => setBpm(event.target.value)} disabled={!editor} /></div>
              <div className="field"><label htmlFor="meter">Meter</label><input id="meter" value={meter} onChange={(event) => setMeter(event.target.value)} disabled={!editor} list="meters" /></div>
            </div>
            <datalist id="meters"><option value="4/4" /><option value="3/4" /><option value="6/8" /><option value="2/4" /><option value="5/4" /></datalist>
            <div className="field"><label htmlFor="ccli">CCLI</label><input id="ccli" value={ccli} onChange={(event) => setCcli(event.target.value)} disabled={!editor} placeholder="Leave blank if this is an original" /></div>
            <div className="field"><label htmlFor="tags">Tags</label><input id="tags" value={tags} onChange={(event) => setTags(event.target.value)} disabled={!editor} placeholder="gathering, evening" /></div>
            <div className="field"><label htmlFor="notes">Notes</label><textarea id="notes" value={notes} onChange={(event) => setNotes(event.target.value)} disabled={!editor} /></div>
            {creating && editor && <div className="field"><label htmlFor="chordpro">ChordPro chart</label><textarea id="chordpro" value={chordpro} onChange={(event) => {
              const chart = event.target.value;
              setChordpro(chart);
              if (!title.trim()) setTitle(titleFromChart(chart));
            }} spellCheck={false} /><span className="faint">Paste ChordPro here. A {"{title: ...}"} line fills the title above. Your draft is kept in this browser until you create the song.</span></div>}
            {editor && <div className="song-editor-actions"><button className="btn primary" type="submit" disabled={busy}>{busy ? "Saving…" : creating ? "Create song" : "Save details"}</button>{error && <ErrorNote>{error}</ErrorNote>}{status && <span role="status">{status}</span>}</div>}
          </form>
          <article className="panel panel-pad stack">
            <div className="cluster">
              <KeyField label="Display key" value={target} onChange={setTarget} />
              <div className="field"><label htmlFor="capo">Capo</label><input id="capo" type="number" min={0} max={11} value={capo} onChange={(event) => setCapo(Math.min(11, Math.max(0, Number(event.target.value) || 0)))} /></div>
              <div className="field">
                <label htmlFor="mode">Chart</label>
                <select id="mode" value={mode} onChange={(event) => setMode(event.target.value as ChartMode)}>{CHART_MODES.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}</select>
              </div>
            </div>
            {preview && <p className="faint">Shapes in {preview.key}{capo > 0 ? ` · capo ${capo} · concert ${preview.concertKey}` : ""}</p>}
            {sourceKey !== safeKey(creating ? defaultKey : chartKey) && <p className="notice">The chart text says {sourceKey}. The arrangement key is {creating ? defaultKey : chartKey}.</p>}
            <div className="chart-stage" style={{ ["--stand-font" as string]: "20px" }}>
              {preview && deferredChart.trim() ? <ChartView blocks={parseChart(preview.chart)} mode={mode} numberKey={preview.key} /> : <p className="muted">Add a ChordPro chart to preview chords and lyrics.</p>}
            </div>
          </article>
        </div>
      )}
      {!creating && editor && song && (
        <form className="panel panel-pad stack" onSubmit={(event) => void saveChart(event)}>
          <div className="split"><h2>Arrangements</h2><button className="btn small" type="button" onClick={() => setAddingChart(true)}>New arrangement</button></div>
          <div className="arrangement-bar">
            {song.arrangements.map((item) => (
              <button key={item.id} type="button" className="btn small" aria-pressed={item.id === arrangementId} onClick={() => fillArrangement(item, song.defaultKey)}>
                {item.name}{item.isDefault ? " · default" : ""}
              </button>
            ))}
          </div>
          <div className="cluster">
            <div className="field"><label htmlFor="chart-name">Name</label><input id="chart-name" value={chartName} onChange={(event) => setChartName(event.target.value)} /></div>
            <KeyField label="Written key" value={chartKey} onChange={setChartKey} />
            <div className="field"><label htmlFor="chart-bpm">BPM</label><input id="chart-bpm" value={chartBpm} onChange={(event) => setChartBpm(event.target.value)} /></div>
            <div className="field"><label htmlFor="chart-meter">Meter</label><input id="chart-meter" value={chartMeter} onChange={(event) => setChartMeter(event.target.value)} /></div>
          </div>
          <div className="field"><label htmlFor="chart-body">ChordPro</label><textarea id="chart-body" value={chordpro} onChange={(event) => setChordpro(event.target.value)} spellCheck={false} /></div>
          <div className="cluster">
            <button className="btn primary" type="submit" disabled={busy}>Save chart</button>
            <button className="btn" type="button" disabled={busy || song.arrangements.find((item) => item.id === arrangementId)?.isDefault} onClick={() => void makeDefault()}>Make default</button>
          </div>
        </form>
      )}
      {!creating && song && (
        <section className="panel panel-pad stack">
          <h2>Files</h2>
          <p className="muted">PDF charts and audio (mp3, wav, ogg, m4a, webm), up to 12 MB. They show on the stand with this song.</p>
          {song.attachments.length === 0 && <p className="faint">No files yet.</p>}
          {song.attachments.map((file) => (
            <div className="file-row" key={file.id}>
              <span><a href={file.url}>{file.label}</a><span className="faint"> · {file.kind} · {fileSize(file.bytes)}</span></span>
              {editor && <button className="btn small danger" type="button" onClick={() => void deleteAttachment(file.id).then(() => getSong(songId)).then(setSong).catch((caught: unknown) => setError(messageOf(caught)))}>Remove</button>}
            </div>
          ))}
          {song.attachments.filter((file) => file.kind === "audio").map((file) => <audio key={`${file.id}-play`} src={file.url} controls preload="none" />)}
          {pdf && <button className="btn small" type="button" onClick={() => setShowPdf((value) => !value)}>{showPdf ? "Hide PDF" : "Preview PDF"}</button>}
          {showPdf && pdf && (
            <Suspense fallback={<p className="muted">Opening the PDF…</p>}>
              <PdfChart url={pdf.url} fileId={pdf.id} page={1} zoom={1.15} night onPages={() => undefined} />
            </Suspense>
          )}
          {editor && (
            <div className="cluster">
              <div className="field"><label htmlFor="file-label">Label</label><input id="file-label" value={label} onChange={(event) => setLabel(event.target.value)} placeholder="Lead chart" /></div>
              <input aria-label="Upload PDF or audio" type="file" accept="application/pdf,audio/*,.pdf,.mp3,.wav,.ogg,.m4a,.webm" onChange={(event) => { const file = event.target.files?.[0]; event.target.value = ""; if (file) void onUpload(file); }} />
            </div>
          )}
        </section>
      )}
      {confirmArchive && (
        <Dialog title="Archive this song?" onClose={() => setConfirmArchive(false)}>
          <p className="muted">It leaves the library and new plans. Existing plan items keep their titles.</p>
          <div className="cluster" style={{ marginTop: 12 }}>
            <button className="btn danger" type="button" onClick={() => void archiveSong(songId).then(() => navigate("/songs")).catch((caught: unknown) => setError(messageOf(caught)))}>Archive song</button>
            <button className="btn ghost" type="button" onClick={() => setConfirmArchive(false)}>Cancel</button>
          </div>
        </Dialog>
      )}
      {addingChart && (
        <Dialog title="New arrangement" onClose={() => setAddingChart(false)}>
          <form className="form-grid" onSubmit={(event) => void addArrangement(event)}>
            <div className="field"><label htmlFor="arr-name">Name</label><input id="arr-name" name="name" defaultValue="Acoustic" required /></div>
            <div className="field"><label htmlFor="arr-key">Key</label><select id="arr-key" name="keyName" defaultValue={defaultKey}>{[...MAJOR_KEY_OPTIONS, ...MINOR_KEY_OPTIONS].map((key) => <option key={key}>{key}</option>)}</select></div>
            <label className="check-line"><input name="copy" type="checkbox" /> Copy the current chart</label>
            <button className="btn primary" type="submit" disabled={busy}>Add arrangement</button>
          </form>
        </Dialog>
      )}
    </section>
  );
}
